import { useCallback, useEffect, useRef, useState } from "react";
import { api, ApiError, staleCopy } from "../api";
import type { SkillDetail } from "../types";
import { StaleNotice } from "../components/StaleNotice";

/**
 * Skill editor for one profile.
 *
 * A skill is a folder holding SKILL.md plus any supporting files, exactly like Claude Code.
 * Only the name and description reach the system prompt; the body is read by the agent when
 * a task matches, and it resolves relative paths against the skill folder — which is why the
 * attachments here can be any format at all, and why the body can just say "see template.md".
 *
 * Everything is written into profiles/<name>/skills/, so it outlives restarts.
 */

const BLANK = { name: "", description: "", body: "" };

function formatSize(bytes: number): string {
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
	return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export function SkillManager({ profile, onClose, onChanged }: { profile: string; onClose: () => void; onChanged: () => void }) {
	const [skills, setSkills] = useState<SkillDetail[] | null>(null);
	const [selected, setSelected] = useState<string | null>(null);
	const [draft, setDraft] = useState(BLANK);
	const [creating, setCreating] = useState(false);
	const [error, setError] = useState<string | null>(null);
	/** A save refused because SKILL.md changed after it was loaded, and what it holds now. */
	const [stale, setStale] = useState<{ current: SkillDetail | null } | null>(null);
	const [busy, setBusy] = useState(false);
	const fileInput = useRef<HTMLInputElement>(null);

	const load = useCallback(
		async (keep?: string) => {
			try {
				const { skills } = await api.skills(profile);
				setSkills(skills);
				const target = skills.find((s) => s.name === keep) ?? skills[0];
				setStale(null);
				if (target && !creating) {
					setSelected(target.name);
					setDraft({ name: target.name, description: target.description, body: target.body });
				}
			} catch (err) {
				setError(err instanceof ApiError ? err.message : "加载 skills 失败");
			}
		},
		[profile, creating],
	);

	useEffect(() => {
		void load();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [profile]);

	const current = skills?.find((s) => s.name === selected);

	function pick(skill: SkillDetail) {
		// The longest free-text field in the product; switching skills must not discard it.
		const edited = current && draft && (draft.body !== current.body || draft.description !== current.description);
		if (edited && !confirm(`「${current.name}」有未保存的修改，确定离开？`)) return;
		setCreating(false);
		setError(null);
		setStale(null);
		setSelected(skill.name);
		setDraft({ name: skill.name, description: skill.description, body: skill.body });
	}

	function startCreate() {
		setCreating(true);
		setError(null);
		setSelected(null);
		setDraft(BLANK);
	}

	/** Save over the version the editor loaded, or over `ifVersion` once the person chose to overwrite. */
	async function save(ifVersion = current?.version) {
		setBusy(true);
		setError(null);
		setStale(null);
		try {
			if (creating) {
				const { skill } = await api.createSkill(profile, draft);
				setCreating(false);
				setSelected(skill.name);
				await load(skill.name);
			} else if (selected) {
				await api.updateSkill(profile, selected, { description: draft.description, body: draft.body, ifVersion });
				await load(selected);
			}
			onChanged();
		} catch (err) {
			const copy = staleCopy<SkillDetail>(err);
			if (copy) setStale(copy);
			else setError(err instanceof ApiError ? err.message : "保存失败");
		} finally {
			setBusy(false);
		}
	}

	async function remove(name: string) {
		if (!confirm(`删除 skill「${name}」？附件将一并删除，无法恢复。`)) return;
		setBusy(true);
		try {
			await api.deleteSkill(profile, name);
			setSelected(null);
			setDraft(BLANK);
			await load();
			onChanged();
		} catch (err) {
			setError(err instanceof ApiError ? err.message : "删除失败");
		} finally {
			setBusy(false);
		}
	}

	async function upload(files: FileList | null) {
		if (!files?.length || !selected) return;
		setBusy(true);
		setError(null);
		try {
			await api.uploadSkillFiles(profile, selected, [...files]);
			await load(selected);
			onChanged();
		} catch (err) {
			setError(err instanceof ApiError ? err.message : "上传失败");
		} finally {
			setBusy(false);
			if (fileInput.current) fileInput.current.value = "";
		}
	}

	async function removeFile(filename: string) {
		if (!selected) return;
		setBusy(true);
		try {
			await api.deleteSkillFile(profile, selected, filename);
			await load(selected);
			onChanged();
		} catch (err) {
			setError(err instanceof ApiError ? err.message : "删除文件失败");
		} finally {
			setBusy(false);
		}
	}

	const editable = creating || Boolean(selected);

	return (
		<div className="modal" role="dialog" aria-label={`${profile} 的 skills`}>
			<div className="modal-card skill-card">
				<header>
					<span>
						<b>{profile}</b> 的 skills
					</span>
					<button className="ghost" onClick={onClose}>
						关闭
					</button>
				</header>

				{error && <p className="banner error">{error}</p>}

				<div className="modal-body">
					<nav className="skill-list">
						<button onClick={startCreate}>新建 skill</button>
						{skills === null && <p className="hint">加载中…</p>}
						{skills?.length === 0 && !creating && <p className="hint">暂无 skill</p>}
						{skills?.map((s) => (
							<button
								key={s.name}
								className={`skill-item${s.name === selected ? " active" : ""}`}
								onClick={() => pick(s)}
							>
								<span className="name">{s.name}</span>
								<span className="meta">
									{s.files.length > 0 ? `${s.files.length} 个附件` : "无附件"}
								</span>
							</button>
						))}
					</nav>

					<div className="skill-edit">
						{!editable ? null : (
							<>
								<label>
									名称
									<input
										value={draft.name}
										disabled={!creating}
										placeholder="meeting-notes"
										onChange={(e) => setDraft({ ...draft, name: e.target.value })}
									/>
								</label>

								<label>
									适用场景
									<textarea
										rows={2}
										value={draft.description}
										onChange={(e) => setDraft({ ...draft, description: e.target.value })}
									/>
								</label>

								<label className="grow">
									正文
									<textarea
										value={draft.body}
										onChange={(e) => setDraft({ ...draft, body: e.target.value })}
									/>
								</label>

								{current && (
									<div className="skill-files">
										<div className="row">
											<span>附件</span>
											<button className="ghost" onClick={() => fileInput.current?.click()} disabled={busy}>
												上传
											</button>
											<input ref={fileInput} type="file" multiple hidden onChange={(e) => void upload(e.target.files)} />
										</div>
										{current.files.length === 0 ? (
											<p className="hint">暂无附件</p>
										) : (
											<ul>
												{current.files.map((f) => (
													<li key={f.name}>
														<code>{f.name}</code>
														<span className="hint">{formatSize(f.size)}</span>
														<button className="ghost" onClick={() => void removeFile(f.name)} disabled={busy}>
															×
														</button>
													</li>
												))}
											</ul>
										)}
									</div>
								)}

								{stale && (
									<StaleNotice onReload={() => void load(selected ?? undefined)} onOverwrite={() => void save(stale.current?.version)} />
								)}
								<div className="skill-actions">
									<button onClick={() => void save()} disabled={busy || !draft.name || !draft.description || !draft.body}>
										{creating ? "创建" : "保存"}
									</button>
									{!creating && selected && (
										<button className="ghost" onClick={() => void remove(selected)} disabled={busy}>
											删除
										</button>
									)}
								</div>
							</>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}
